import dayjs from 'dayjs';

import { IMinimumStay } from '@/services/types/search/minimumStay';
import formatDate from '@/utility/format-date';

// The listing/checkout calendars let the guest navigate up to 2 years out (see
// AvailabilityCalendar's toMonth), so both fetch sites (the redux thunk and the SWR hook) must
// request the same window — otherwise a start date in month 13-24 resolves seasonally on one
// surface and falls back to the flat default on the other. Shared here so they can't drift apart.
export const getMinimumStayDateRange = () => ({
  fromDate: formatDate(dayjs().toDate(), 'YYYY-MM-DD'),
  toDate: formatDate(dayjs().add(2, 'year').toDate(), 'YYYY-MM-DD'),
});

type TMinimumStayBreakdownEntry = {
  from: string;
  to: string;
  duration: number;
};

export type TMinimumStayRequirement = {
  duration: number;
  use_day_pricing: boolean;
  breakdown?: TMinimumStayBreakdownEntry[];
};

type TResolveMinimumStayRequirementArgs = {
  useDayPricing: boolean;
  // active_options.minimum_days — always the resolved/default value, since it's correct for
  // today. The new endpoint's top-level `minimum_requirement` is resolved over the whole
  // queried range (e.g. today -> +2 years), so it isn't today's value — only `breakdown`
  // (per-date lookup) is used from the endpoint.
  defaultDuration: number | undefined;
  minimumStayData: IMinimumStay | undefined;
  hasError: boolean;
};

// active_options.minimum_days is always the duration (it's correct for today). breakdown is
// layered on top so callers (calendar, listing-page badge) can resolve a season-specific
// minimum per selected date (see getEffectiveMinimumStay) — falling back to `duration` itself
// when no interval matches, or when the fetch failed/404'd/hasn't resolved yet.
export const resolveMinimumStayRequirement = ({
  useDayPricing,
  defaultDuration,
  minimumStayData,
  hasError,
}: TResolveMinimumStayRequirementArgs): TMinimumStayRequirement | undefined => {
  if (defaultDuration === undefined) return undefined;

  const breakdown =
    !hasError && minimumStayData?.breakdown?.length
      ? minimumStayData.breakdown.map(entry => ({
          from: entry.from,
          to: entry.to,
          duration: entry.minimum_requirement,
        }))
      : undefined;

  return { duration: defaultDuration, use_day_pricing: useDayPricing, breakdown };
};

// An Invalid Date sneaks past the earlier `date ?` truthiness checks because JS treats it as
// a real object; every dayjs comparison against it then returns false (NaN semantics), which
// would silently make .find match `breakdown[0]`. Callers on the badge path filter upstream,
// but calendar callers (useBillSelectionStep, StickyNavContainer) don't, so both helpers
// re-check here to keep behaviour consistent regardless of who's calling.
const asValidDate = (date: Date | undefined): Date | undefined =>
  date && !Number.isNaN(date.getTime()) ? date : undefined;

// Resolves a requirement that may vary by season down to a flat one for a specific date: if
// `date` falls inside a `breakdown` entry, that entry's duration wins; otherwise (no date, no
// breakdown, or date outside every entry) falls back to the flat `duration`. This is the
// per-date semantic the calendar needs — start rejection, day modifiers, tooltips all resolve
// one day at a time. For the listing-page badge use getRangeMinimumStay instead: the API
// enforces the max minimum across the guest's whole booking range, so a start-date-only
// resolution can still disagree with the booking-flow error message when the range spans a
// seasonal boundary.
export const getEffectiveMinimumStay = (
  minimumStayRequirement: TMinimumStayRequirement | undefined,
  date: Date | undefined,
): TMinimumStayRequirement | undefined => {
  if (!minimumStayRequirement) return undefined;

  const { breakdown, use_day_pricing } = minimumStayRequirement;
  const validDate = asValidDate(date);

  const match =
    validDate && breakdown?.length
      ? breakdown.find(
          entry =>
            !dayjs(validDate).isBefore(entry.from, 'day') &&
            !dayjs(validDate).isAfter(entry.to, 'day'),
        )
      : undefined;

  return {
    duration: match?.duration ?? minimumStayRequirement.duration,
    // breakdown entries never carry their own use_day_pricing — that's a listing-level flag,
    // so it always comes off the parent requirement.
    use_day_pricing,
  };
};

// Resolves the requirement over the guest's whole booking range, matching java-api's
// CalculationLogic which takes the max minimum across every breakdown entry overlapping the
// range. Falls back to per-date behaviour when there's no `to` (single-date lookup), and to the
// flat `duration` when nothing overlaps or there's no `from`. Invalid `Date` instances are
// filtered in `asValidDate` so callers don't have to.
//
// Returns `{ duration, use_day_pricing }` without a `breakdown` — this is the flat shape
// downstream consumers (badge, bill notifications) want. Deliberate, not incidental:
// getEffectiveMinimumStay does the same.
export const getRangeMinimumStay = (
  minimumStayRequirement: TMinimumStayRequirement | undefined,
  from: Date | undefined,
  to: Date | undefined,
): TMinimumStayRequirement | undefined => {
  if (!minimumStayRequirement) return undefined;

  const { breakdown, use_day_pricing, duration } = minimumStayRequirement;
  const validFrom = asValidDate(from);
  const validTo = asValidDate(to);

  if (!validFrom || !breakdown?.length) {
    return getEffectiveMinimumStay(minimumStayRequirement, validFrom);
  }

  // Match java-api's adjustBookingDates: nightly pricing drops the checkout day (Aug 11 → Aug
  // 15 = 4 nights on Aug 11-14), day pricing keeps it (5 days). Without a valid `to` we behave
  // like getEffectiveMinimumStay, which is what the calendar already relies on for its
  // per-date modifiers before an end date has been picked.
  const rangeStart = dayjs(validFrom);
  const rangeEnd = validTo
    ? use_day_pricing
      ? dayjs(validTo)
      : dayjs(validTo).subtract(1, 'day')
    : rangeStart;

  if (rangeEnd.isBefore(rangeStart, 'day')) {
    return getEffectiveMinimumStay(minimumStayRequirement, validFrom);
  }

  const overlapping = breakdown.filter(
    entry =>
      !dayjs(entry.from).isAfter(rangeEnd, 'day') && !dayjs(entry.to).isBefore(rangeStart, 'day'),
  );

  const resolved = overlapping.length
    ? Math.max(...overlapping.map(entry => entry.duration))
    : duration;

  return { duration: resolved, use_day_pricing };
};
